import { useEffect, useState } from 'react';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  isSameMonth,
  isSameDay
} from 'date-fns';
import { es } from 'date-fns/locale';
import api from '../../api/axios';
import { getToken } from '../../utils/auth';
import Loader from '../../components/Loader';
import Modal from 'react-modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

Modal.setAppElement('#root');

export default function CalendarioAdmin() {
  const [vistaActual, setVistaActual] = useState('dia');
  const [fechaReferencia, setFechaReferencia] = useState(new Date());
  const [fechasVisibles, setFechasVisibles] = useState([]);
  const [citasPorDia, setCitasPorDia] = useState({});
  const [excepciones, setExcepciones] = useState({});
  const [loading, setLoading] = useState(true);

  // Ajuste dinámico (fallback a 12h)
  const [horasMinCancel, setHorasMinCancel] = useState(12);

  // Modal
  const [modalCita, setModalCita] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [horasDisponibles, setHorasDisponibles] = useState([]);
  const [loadingHoras, setLoadingHoras] = useState(false);
  const [modalFecha, setModalFecha] = useState(null);
  const [modalHora, setModalHora] = useState('');
  const [modalServicio, setModalServicio] = useState('');
  const [errorModal, setErrorModal] = useState(null);

  // Estados para Transferir / Intercambiar
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioTransferencia, setUsuarioTransferencia] = useState('');
  const [citaIntercambio, setCitaIntercambio] = useState('');

  // Filtros de búsqueda
  const [filtroTransferencia, setFiltroTransferencia] = useState('');
  const [filtroIntercambio, setFiltroIntercambio] = useState('');

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const cargarUsuarios = async () => {
    try {
      const token = getToken();
      const res = await api.get('/admin/clientes', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsuarios(res.data.usuarios || []);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    }
  };

  const transferirCita = async () => {
    // Bloquear transferencia si la cancelación no está disponible por configuración
    if (!puedeCancelarCita()) {
      toast.warn('No se puede transferir: la cancelación no está disponible por configuración.');
      return;
    }
    if (!usuarioTransferencia) return;
    const confirmacion = window.confirm('¿Seguro que deseas transferir esta cita? Se enviarán correos a ambos usuarios.');
    if (!confirmacion) return;

    try {
      const token = getToken();
      await api.post('/admin/cita/transferir', {
        citaId: modalCita.id,
        nuevoUsuarioId: usuarioTransferencia
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cita transferida correctamente');
      setModalCita(null);
      setUsuarioTransferencia('');
      cargarCitas();
    } catch (err) {
      console.error('Error al transferir cita:', err);
      toast.error('Error al transferir la cita');
    }
  };

  const intercambiarCita = async () => {
    // Bloquear intercambio si la cancelación no está disponible por configuración
    if (!puedeCancelarCita()) {
      toast.warn('No se puede intercambiar: la cancelación no está disponible por configuración.');
      return;
    }
    if (!citaIntercambio) return;
    const confirmacion = window.confirm('¿Seguro que deseas intercambiar estas citas? Se notificarán los cambios a ambos usuarios.');
    if (!confirmacion) return;

    try {
      const token = getToken();
      await api.post('/admin/cita/intercambiar', {
        citaId1: modalCita.id,
        citaId2: citaIntercambio
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Citas intercambiadas correctamente');
      setModalCita(null);
      setCitaIntercambio('');
      cargarCitas();
    } catch (err) {
      console.error('Error al intercambiar citas:', err);
      toast.error('Error al intercambiar las citas');
    }
  };

  // ---------- Cargar AJUSTE horas_minimas_cancelacion ----------
  useEffect(() => {
    const token = getToken();
    if (!token) return;

    api.get('/ajustes', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const valor = Number(res.data?.horas_minimas_cancelacion);
        if (!Number.isNaN(valor) && valor > 0) setHorasMinCancel(valor);
      })
      .catch(err => {
        console.error('❌ Error al cargar horas_minimas_cancelacion:', err);
        // mantenemos fallback 12h
      });
  }, []);

  // ---------- Cargar Citas ----------
  const cargarCitas = () => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    const anioMes = format(fechaReferencia, 'yyyy-MM');
    api.get(`/admin/citas?mes=${anioMes}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        const agrupadas = {};
        res.data.citas.forEach(cita => {
          const fecha = cita.fecha;
          if (!agrupadas[fecha]) agrupadas[fecha] = [];
          agrupadas[fecha].push(cita);
        });
        setCitasPorDia(agrupadas);
        setLoading(false);
      })
      .catch(err => {
        console.error('❌ Error al cargar citas:', err);
        setLoading(false);
      });
  };

  useEffect(cargarCitas, [fechaReferencia]);

  // ---------- Cargar Excepciones ----------
  useEffect(() => {
    api.get('/excepciones')
      .then(res => setExcepciones(res.data.excepciones || {}))
      .catch(err => {
        console.error('❌ Error al cargar excepciones:', err);
        setExcepciones({});
      });
  }, []);

  // ---------- Calcular rango visible ----------
  useEffect(() => {
    let inicio, fin;
    if (vistaActual === 'mes') {
      inicio = startOfWeek(startOfMonth(fechaReferencia), { weekStartsOn: 1 });
      fin = endOfWeek(endOfMonth(fechaReferencia), { weekStartsOn: 1 });
    } else if (vistaActual === 'semana') {
      inicio = startOfWeek(fechaReferencia, { weekStartsOn: 1 });
      fin = endOfWeek(fechaReferencia, { weekStartsOn: 1 });
    } else {
      inicio = fechaReferencia;
      fin = fechaReferencia;
    }

    const dias = [];
    let dia = inicio;
    while (dia <= fin) {
      dias.push(dia);
      dia = addDays(dia, 1);
    }
    setFechasVisibles(dias);
  }, [fechaReferencia, vistaActual]);

  const cambiarFecha = (direccion) => {
    if (vistaActual === 'mes') {
      setFechaReferencia(prev => new Date(prev.getFullYear(), prev.getMonth() + direccion, 1));
    } else {
      setFechaReferencia(prev => addDays(prev, direccion * (vistaActual === 'semana' ? 7 : 1)));
    }
  };

  const getEstadoDia = (fechaStr, citas = []) => {
    const excepcionDelDia = excepciones[fechaStr];
    const estaCerrado = excepcionDelDia?.every(e => e.activo === false);
    if (estaCerrado && citas.length > 0) return 'warning';
    if (estaCerrado) return 'cerrado';
    if (citas.length > 0) return 'completo';
    return '';
  };

  const horasDelDia = Array.from({ length: 14 }, (_, i) => i + 8);

  const abrirModal = (cita) => {
    setModalCita(cita);
    setModalFecha(new Date(cita.fecha));
    setModalHora(cita.hora_inicio);
    setModalServicio(cita.Servicio?.id);
    cargarServicios();
  };

  const cargarServicios = async () => {
    const res = await api.get('/servicios');
    setServicios(res.data.servicios || []);
  };

  useEffect(() => {
    if (!modalFecha || !modalServicio) return;
    const fechaStr = format(modalFecha, 'yyyy-MM-dd');
    setLoadingHoras(true);
    api.get(`/disponibilidad/${fechaStr}/${modalServicio}`)
      .then(res => {
        setHorasDisponibles(res.data.huecos || []);
        setLoadingHoras(false);
      })
      .catch(err => {
        console.error('❌ Error al cargar horas disponibles:', err);
        setHorasDisponibles([]);
        setLoadingHoras(false);
      });
  }, [modalFecha, modalServicio]);

  const guardarCambios = async () => {
    // Bloquear modificaciones si la cancelación no está disponible por configuración
    if (!puedeCancelarCita()) {
      toast.warn('No se pueden modificar citas: la cancelación no está disponible por configuración.');
      return;
    }
    const token = getToken();
    const citaOriginal = modalCita;
    const nuevaFecha = format(modalFecha, 'yyyy-MM-dd');

    const fechaModificada = citaOriginal.fecha !== nuevaFecha;
    const horaModificada = citaOriginal.hora_inicio !== modalHora;
    const servicioModificado = citaOriginal.Servicio?.id !== modalServicio;

    const cambiosRealizados = fechaModificada || horaModificada || servicioModificado;

    if (!modalHora) {
      toast.error('Selecciona una hora para la cita.');
      return;
    }

    if (!cambiosRealizados) {
      toast.info('No se ha modificado la cita.');
      setModalCita(null);
      return;
    }

    try {
      await api.put(`/admin/citas/${modalCita.id}`, {
        fecha: nuevaFecha,
        hora_inicio: modalHora,
        servicioId: modalServicio
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cita actualizada correctamente');
      setModalCita(null);
      cargarCitas();
    } catch (error) {
      setErrorModal('Error al actualizar la cita.');
      toast.error('Hubo un error al actualizar la cita');
      console.log(error);
    }
  };

  // ---------- Política de cancelación dinámica ----------
  const puedeCancelarCita = () => {
    if (!modalCita) return false;
    // Permitir siempre si el usuario logueado es el especial
    const telefonoLogueado = localStorage.getItem('telefono');
    if (telefonoLogueado === '34636545966') return true;
    const fechaCita = new Date(modalCita.fecha);
    const [horaStr, minutoStr] = modalCita.hora_inicio.split(':');
    fechaCita.setHours(Number(horaStr));
    fechaCita.setMinutes(Number(minutoStr));
    fechaCita.setSeconds(0);

    const ahora = new Date();
    const diferenciaHoras = (fechaCita - ahora) / (1000 * 60 * 60);
    return diferenciaHoras >= horasMinCancel;
  };

  const cancelarCita = async () => {
    const confirmacion = window.confirm(`¿Estás seguro de que quieres cancelar esta cita?`);
    if (!confirmacion) return;

    const token = getToken();

    try {
      await api.post('/citas/cancelar-con-cliente', {
        citaId: modalCita.id,
        nombre: modalCita.Usuario?.nombre || 'Usuario eliminado',
        apellidos: modalCita.Usuario?.apellidos || '',
        telefono: modalCita.Usuario?.telefono || '',
        fecha: modalCita.fecha,
        hora_inicio: modalCita.hora_inicio,
        servicio: modalCita.Servicio
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Cita cancelada correctamente');
      setModalCita(null);
      cargarCitas();
    } catch (error) {
      setErrorModal('Error al cancelar la cita.');
      toast.error('Hubo un error al cancelar la cita');
      console.error(error);
    }
  };

  // ---------- Iconos especiales por día (igual que en el calendario público) ----------
  const renderIconoEspecial = (fecha) => {
    const dia = fecha.getDate();
    const mes = fecha.getMonth(); // 0 = enero, 11 = diciembre
    const anio = fecha.getFullYear();

    // Ejemplo: diciembre 2025
    if (mes === 11 && anio === 2025) {
      if (dia === 24) {
        return (
          <img
            src="/images/arbol-de-navidad.png"
            alt="Navidad"
            className="img-calendario-evento"
          />
        );
      }
      if (dia === 31) {
        return (
          <img
            src="/images/salud.png"
            alt="Año nuevo"
            className="img-calendario-evento"
          />
        );
      }
      /*
      if (dia >= 11 && dia <= 20) {
        return (
          <img
            src="/images/abanico.png"
            alt="Feria La Línea"
            className="img-calendario-evento-feria"
          />
        );
      }
      */
    }

    if (mes === 0 && anio === 2026) {
      if (dia === 6) {
        return <img src="/images/los-tres-reyes-magos.svg" alt="Reyes Magos" className="img-calendario-evento" />;
      }
    }

    if (mes === 6 && anio === 2026) {
      if (dia === 17) {
        return <img src="/images/corona.svg" alt="Feria La Línea" className="img-calendario-evento-feria-normal" />;
      }
      if (dia === 22) {
        return <img src="/images/mujer.svg" alt="Feria La Línea" className="img-calendario-evento-feria-normal" />;
      }
      if ((dia >= 18 && dia <= 21) || (dia >= 23 && dia <= 25)) {
        return <img src="/images/abanico.png" alt="Feria La Línea" className="img-calendario-evento-feria" />;
      }
      if (dia === 26) {
        return <img src="/images/fuegos-artificiales.svg" alt="Feria La Línea" className="img-calendario-evento-feria-normal" />;
      }
    }

    return null;
  };

  const renderMiniCalendario = () => {
    const inicio = startOfWeek(startOfMonth(fechaReferencia), { weekStartsOn: 1 });
    const fin = endOfWeek(endOfMonth(fechaReferencia), { weekStartsOn: 1 });

    const dias = [];
    let dia = inicio;

    while (dia <= fin) {
      dias.push(new Date(dia));
      dia = addDays(dia, 1);
    }

    const fechaActualStr = format(fechaReferencia, 'yyyy-MM-dd');

    const estaCerrado = (fechaStr) => {
      const excepcion = excepciones[fechaStr];
      return excepcion?.length > 0 && excepcion.every(e => e.activo === false);
    };

    return (
      <div className='grid-calendario mini-calendario'>
        <div className='calendario-header'>
          <button onClick={() => setFechaReferencia(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}>
            <img src='/images/izquierda.svg' alt='Anterior' />
          </button>
          <span className='calendario-mes'>
            {format(fechaReferencia, 'MMMM yyyy', { locale: es })}
          </span>
          <button onClick={() => setFechaReferencia(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}>
            <img src='/images/derecha.svg' alt='Siguiente' />
          </button>
        </div>

        <div className='dias-de-la-semana' style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
          {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((dia) => (
            <div key={dia}><strong>{dia}</strong></div>
          ))}

          {dias.map((fecha, i) => {
            const fechaStr = format(fecha, 'yyyy-MM-dd');
            const seleccionado = fechaStr === fechaActualStr;
            const fueraMes = fecha.getMonth() !== fechaReferencia.getMonth();
            const cerrado = estaCerrado(fechaStr);
            const tieneCitas = !!citasPorDia[fechaStr]?.length;
            const warning = cerrado && tieneCitas;

            return (
              <button
                key={i}
                className={`calendario-boton 
                  ${fueraMes ? 'calendario-boton-disabled' : ''} 
                  ${seleccionado ? 'seleccionado' : ''}`}
                disabled={cerrado && !tieneCitas}
                onClick={() => setFechaReferencia(fecha)}
                style={{
                  backgroundColor: seleccionado ? '#25d366' : warning ? '#fff3cd' : 'white',
                  color: seleccionado ? 'white' : 'black',
                  opacity: fueraMes ? 0.3 : 1,
                  cursor: 'pointer',
                  position: 'relative'
                }}
                title={cerrado ? 'Día cerrado por excepción' : ''}
              >
                {format(fecha, 'd')}
                {renderIconoEspecial(fecha)}

                {tieneCitas && (
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      backgroundColor: '#25d366',
                      borderRadius: '50%',
                      display: 'block',
                      margin: '4px auto 0 auto'
                    }}
                  ></span>
                )}
                {cerrado && (
                  <span style={{ fontSize: '8px', display: 'block', marginTop: '2px' }}>
                    Cerrado
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const accionesBloqueadas = !puedeCancelarCita();

  return (
    <div className="calendario-admin-container site-width">
      <div className="texto-dashboard">
        <h1>Calendario de citas</h1>
        <p>Visualiza y gestiona tus citas con un solo click.</p>
      </div>
      <div className="vista-selector">
        <button onClick={() => setVistaActual('dia')} className={vistaActual === 'dia' ? 'activo' : ''}>Día</button>
        <button onClick={() => setVistaActual('semana')} className={vistaActual === 'semana' ? 'activo' : ''}>Semana</button>
        <button onClick={() => setVistaActual('mes')} className={vistaActual === 'mes' ? 'activo' : ''}>Mes</button>
      </div>

      <div className="calendario-header calendario-header-pc">
        <button onClick={() => cambiarFecha(-1)}><img src="/images/izquierda.svg" alt="Anterior" /></button>
        <h2>
          {vistaActual === 'dia'
            ? format(fechaReferencia, 'd MMMM yyyy', { locale: es })
            : format(fechaReferencia, 'MMMM yyyy', { locale: es })}
        </h2>
        <button onClick={() => cambiarFecha(1)}><img src="/images/derecha.svg" alt="Siguiente" /></button>
      </div>

      {loading ? (
        <Loader />
      ) : vistaActual === 'dia' ? (
        <>
          {renderMiniCalendario()}

          {(() => {
            const fechaStr = format(fechaReferencia, 'yyyy-MM-dd');
            const citas = citasPorDia[fechaStr] || [];
            const estado = getEstadoDia(fechaStr, citas);

            if (estado === 'warning') {
              return (
                <div className="aviso-warning-dia">
                  <p>⚠️ ¡Ojo! Este día está cerrado, pero tienes citas programadas.</p>
                </div>
              );
            }
            return null;
          })()}

          <div className="vista-dia">
            {horasDelDia.map(hora => {
              const horaTexto = `${hora.toString().padStart(2, '0')}:00`;
              const fechaStr = format(fechaReferencia, 'yyyy-MM-dd');
              const citas = (citasPorDia[fechaStr]?.filter(cita =>
                parseInt(cita.hora_inicio.split(':')[0], 10) === hora
              ) || []).sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio));

              return (
                <div key={hora} className="bloque-hora">
                  <div className="hora-label">{horaTexto}</div>
                  <div className="bloque-de-citas">
                    {citas.length > 0 ? (
                      citas.map((cita, i) => {
                        const inicio = new Date(`1970-01-01T${cita.hora_inicio}`);
                        const fin = new Date(inicio.getTime() + (cita.Servicio?.duracion || 0) * 60000);
                        return (
                          <div key={i} className="bloque-cita" onClick={() => abrirModal(cita)}>
                            {cita.asistencia === 'pendiente_pagar' && (
                              <p className="aviso-pendiente-pago">⚠️ ¡Ojo! Este cliente no acudió a su anterior cita. (Debe abonar mitad de ese servicio)</p>
                            )}
                            <p className='servicio-cita'>{cita.Servicio?.nombre} - {cita.precio_cita ?? cita.Servicio?.precio}€</p>
                            <p className="cliente-cita">
                              {cita.Usuario ? `${cita.Usuario.nombre} ${cita.Usuario.apellidos}` : '(Usuario eliminado)'}
                            </p>
                            <p className='hora-cita'>{format(inicio, 'HH:mm aaaa')} – {format(fin, 'HH:mm aaaa')}</p>
                          </div>
                        );
                      })
                    ) : (
                      <div className="sin-cita">-</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className={`grid-${vistaActual}`}>
          {vistaActual !== 'dia' && ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(dia => (
            <div key={dia} className="nombre-dia">{dia}</div>
          ))}
          {fechasVisibles.map((fecha, i) => {
            const fechaStr = format(fecha, 'yyyy-MM-dd');
            const citas = citasPorDia[fechaStr] || [];
            const estado = getEstadoDia(fechaStr, citas);

            return (
              <div
                key={i}
                className={`celda-dia ${!isSameMonth(fecha, fechaReferencia) && vistaActual === 'mes' ? 'fuera-mes' : ''} ${isSameDay(fecha, new Date()) ? 'hoy' : ''} ${estado}`}
              >
                <div className="cabecera-dia">
                  {format(fecha, 'd')}
                  {renderIconoEspecial(fecha)}
                </div>

                {estado === 'cerrado' && <p className='cerrado-texto'>Cerrado</p>}
                <div className="bloque-de-citas">
                  {citas.map((cita, index) => {
                    const inicio = new Date(`1970-01-01T${cita.hora_inicio}`);
                    const duracionMin = cita.Servicio?.duracion || 0;
                    const fin = new Date(inicio.getTime() + duracionMin * 60000);

                    return (
                      <div key={index} className="bloque-cita" onClick={() => abrirModal(cita)}>
                        {cita.asistencia === 'pendiente_pagar' && (
                          <p className="aviso-pendiente-pago">⚠️ ¡Ojo! Este cliente no acudió a su anterior cita. (Debe abonar mitad de ese servicio)</p>
                        )}
                        <p className='hora-cita'>
                          {format(inicio, 'HH:mm aaaa')} – {format(fin, 'HH:mm aaaa')}
                        </p>
                        <p className='servicio-cita'><b>Servicio:</b> {cita.Servicio?.nombre}</p>
                        <p className="precio-cita"><b>Precio: </b>{cita.precio_cita ?? cita.Servicio?.precio}€</p>
                        <p className="cliente-cita">
                          <b>Cliente:</b> {cita.Usuario ? `${cita.Usuario.nombre} ${cita.Usuario.apellidos}` : '(Usuario eliminado)'}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL */}
      <Modal
        isOpen={!!modalCita}
        onRequestClose={() => setModalCita(null)}
        className="modal-editar-cita"
        overlayClassName="modal-overlay"
      >
        {/** Calcula accionesBloqueadas dinámicamente para el modal actual */}
        <>
          {(() => {
            const accionesBloqueadas = !puedeCancelarCita();
            return (
              <>
                <h2>
                  Editando la cita de {modalCita?.Usuario
                    ? `${modalCita.Usuario.nombre} ${modalCita.Usuario.apellidos}`
                    : '(Usuario eliminado)'}
                </h2>

                {modalCita?.Usuario ? (
                  <div className="datos-cliente-edicion">
                    {modalCita.Usuario.email ? (
                      <p>Se notificará por email al cliente en caso de modificar su cita.</p>
                    ) : (
                      <p className='warning-msg-edicion'>CONSEJO: Este cliente no tiene un email definido. Deberías notificarlo vía WhatsApp o llamada.</p>
                    )}
                    <div className="info-edicion">
                      {modalCita.Usuario.email && (
                        <p><strong>Email:</strong> {modalCita.Usuario.email}</p>
                      )}
                      {modalCita.Usuario.telefono && (
                        <p>
                          <strong>Teléfono: </strong>{' '}
                          <a
                            href={`https://wa.me/${modalCita.Usuario.telefono}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#25d366', textDecoration: 'none', textDecorationLine: 'underline' }}
                          >
                            +{modalCita.Usuario.telefono}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="datos-cliente-edicion">
                    <p className="warning-msg">Este usuario ha sido eliminado. La cita se mantendrá pero sin datos de contacto.</p>
                  </div>
                )}

                {errorModal && <p className="error">{errorModal}</p>}

                <div className="opciones-modal">
                  <div className="label-form">
                    <label>Fecha</label>
                    <DatePicker
                      selected={modalFecha}
                      onChange={(fecha) => {
                        setModalFecha(fecha);
                        setModalHora(''); // reinicia la hora al cambiar la fecha
                      }}
                      dateFormat="dd/MM/yyyy"
                      locale={es}
                      minDate={new Date()}
                      disabled={accionesBloqueadas}
                      // atenuar si está bloqueado
                      popperProps={{ style: { opacity: accionesBloqueadas ? 0.6 : 1 } }}
                    />
                  </div>

                  <div className="label-form">
                    <label>Servicio</label>
                    <select
                      value={modalServicio}
                      onChange={e => setModalServicio(Number(e.target.value))}
                      disabled={accionesBloqueadas}
                      title={accionesBloqueadas ? `Modificación deshabilitada (cancelación requiere mínimo ${horasMinCancel} h)` : ''}
                      style={{ opacity: accionesBloqueadas ? 0.6 : 1 }}
                    >
                      <option value="">Selecciona</option>
                      {servicios.map(serv => (
                        <option key={serv.id} value={serv.id}>{serv.nombre}</option>
                      ))}
                    </select>
                  </div>

                  <div className="label-form">
                    <label>Hora <span>(En base a disponibilidad)</span></label>
                    {loadingHoras ? <p className='cargando-horas'>Cargando disponibilidad...</p> : (
                      <select
                        value={modalHora}
                        onChange={e => setModalHora(e.target.value)}
                        disabled={accionesBloqueadas}
                        title={accionesBloqueadas ? `Modificación deshabilitada (cancelación requiere mínimo ${horasMinCancel} h)` : ''}
                        style={{ opacity: accionesBloqueadas ? 0.6 : 1 }}
                      >
                        <option value="">Selecciona</option>
                        {horasDisponibles.map((h, i) => (
                          <option key={i} value={h}>{h}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                <div className="botones-modal-editar">
                  <button
                    onClick={guardarCambios}
                    disabled={!modalHora || !modalServicio || accionesBloqueadas}
                    title={accionesBloqueadas ? `Modificación deshabilitada (cancelación requiere mínimo ${horasMinCancel} h)` : ''}
                    style={{ opacity: (!modalHora || !modalServicio || accionesBloqueadas) ? 0.6 : 1 }}
                  >
                    Guardar
                  </button>

                  {!accionesBloqueadas ? (
                    <button onClick={cancelarCita} className="cancelar">Cancelar cita</button>
                  ) : (
                    <button
                      className="cancelar"
                      disabled
                      title={`La cancelación requiere al menos ${horasMinCancel} h de antelación`}
                      style={{ opacity: 0.5 }}
                    >
                      Cancelación no disponible (mín. {horasMinCancel} h)
                    </button>
                  )}

                  <button onClick={() => setModalCita(null)} className="cerrar">Cerrar</button>
                </div>
              </>
            );
          })()}
        </>

        {/* SECCIÓN DE ACCIONES AVANZADAS (Transferir / Intercambiar) */}
        <div className="acciones-avanzadas-modal">
          <h3>Acciones Avanzadas</h3>

          {/* TRANSFERIR CITA */}
          <div className="bloque-accion">
            <h4>Transferir Cita</h4>
            <p className="desc-accion">Asigna esta cita a otro usuario. Se notificará por email al cliente que la recibe.</p>

            <input
              type="text"
              placeholder="Buscar por nombre, teléfono o email..."
              value={filtroTransferencia}
              onChange={(e) => setFiltroTransferencia(e.target.value)}
              className="input-filtro"
              style={{ padding: '8px', marginBottom: '5px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px', opacity: accionesBloqueadas ? 0.6 : 1 }}
              disabled={accionesBloqueadas}
              title={accionesBloqueadas ? `Transferencia deshabilitada (cancelación requiere mínimo ${horasMinCancel} h)` : ''}
            />

            <div className="form-accion">
              <select
                value={usuarioTransferencia}
                onChange={(e) => setUsuarioTransferencia(e.target.value)}
                disabled={accionesBloqueadas}
                title={accionesBloqueadas ? `Transferencia deshabilitada (cancelación requiere mínimo ${horasMinCancel} h)` : ''}
                style={{ opacity: accionesBloqueadas ? 0.6 : 1 }}
              >
                <option value="">Seleccionar nuevo usuario</option>
                {usuarios
                  .filter(u => {
                    if (!filtroTransferencia) return true;
                    const texto = filtroTransferencia.toLowerCase();
                    return (
                      u.nombre?.toLowerCase().includes(texto) ||
                      u.apellidos?.toLowerCase().includes(texto) ||
                      u.email?.toLowerCase().includes(texto) ||
                      u.telefono?.includes(texto)
                    );
                  })
                  .map(u => (
                    <option key={u.id} value={u.id}>
                      {u.nombre} {u.apellidos} - {u.telefono} - {u.email || 'Sin email'}
                    </option>
                  ))
                }
              </select>
              <button
                className="boton-accion"
                onClick={transferirCita}
                disabled={!usuarioTransferencia || accionesBloqueadas}
                title={accionesBloqueadas ? `Transferencia deshabilitada (cancelación requiere mínimo ${horasMinCancel} h)` : ''}
                style={{ opacity: (!usuarioTransferencia || accionesBloqueadas) ? 0.6 : 1 }}
              >
                Transferir
              </button>
            </div>
          </div>

          {/* INTERCAMBIAR CITA */}
          <div className="bloque-accion">
            <h4>Intercambiar Cita</h4>
            <p className="desc-accion">Intercambia el horario con otra cita existente. Se notificará por email a ambos clientes.</p>

            <input
              type="text"
              placeholder="Buscar por cliente, fecha, hora o servicio..."
              value={filtroIntercambio}
              onChange={(e) => setFiltroIntercambio(e.target.value)}
              className="input-filtro"
              style={{ padding: '8px', marginBottom: '5px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px', opacity: accionesBloqueadas ? 0.6 : 1 }}
              disabled={accionesBloqueadas}
              title={accionesBloqueadas ? `Intercambio deshabilitado (cancelación requiere mínimo ${horasMinCancel} h)` : ''}
            />

            <div className="form-accion">
              <select
                value={citaIntercambio}
                onChange={(e) => setCitaIntercambio(e.target.value)}
                disabled={accionesBloqueadas}
                title={accionesBloqueadas ? `Intercambio deshabilitado (cancelación requiere mínimo ${horasMinCancel} h)` : ''}
                style={{ opacity: accionesBloqueadas ? 0.6 : 1 }}
              >
                <option value="">Seleccionar cita para intercambio</option>
                {Object.values(citasPorDia).flat()
                  .filter(c => c.id !== modalCita?.id && new Date(c.fecha) >= new Date()) // Solo futuras y no la actual
                  .filter(c => {
                    if (!filtroIntercambio) return true;
                    const texto = filtroIntercambio.toLowerCase();
                    const fechaStr = format(new Date(c.fecha), 'dd/MM/yyyy');
                    const nombreCliente = c.Usuario ? `${c.Usuario.nombre} ${c.Usuario.apellidos}` : '';

                    return (
                      nombreCliente.toLowerCase().includes(texto) ||
                      fechaStr.includes(texto) ||
                      c.hora_inicio.includes(texto) ||
                      c.Servicio?.nombre.toLowerCase().includes(texto)
                    );
                  })
                  .sort((a, b) => new Date(`${a.fecha}T${a.hora_inicio}`) - new Date(`${b.fecha}T${b.hora_inicio}`))
                  .map(c => (
                    <option key={c.id} value={c.id}>
                      {c.Usuario?.nombre} {c.Usuario?.apellidos} | {format(new Date(c.fecha), 'dd/MM/yyyy')} - {c.hora_inicio} | {c.Servicio?.nombre}
                    </option>
                  ))
                }
              </select>
              <button
                className="boton-accion"
                onClick={intercambiarCita}
                disabled={!citaIntercambio || accionesBloqueadas}
                title={accionesBloqueadas ? `Intercambio deshabilitado (cancelación requiere mínimo ${horasMinCancel} h)` : ''}
                style={{ opacity: (!citaIntercambio || accionesBloqueadas) ? 0.6 : 1 }}
              >
                Intercambiar
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
}
